import { ActionButton } from '@if/mui'
import { Stack } from '@mui/material'
import { REPLAY_MIGRATION_COUNTDOWN_SECONDS } from '@src/types/constants'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * How long the host snackbar stays up. It deliberately outlives the countdown so the
 * message can report what the timer did — replaying, or called off — before it fades.
 */
export const REPLAY_SNACKBAR_DURATION = (REPLAY_MIGRATION_COUNTDOWN_SECONDS + 1) * 1000

interface ReplayMigrationEventsCountdownProps {
  onElapsed: () => void
  onCancel: () => void
}

/**
 * The message inside the replay-migration snackbar: the notice and the undo window in
 * one. It counts the seconds down and calls `onElapsed` when they run out, so the replay
 * is the default and cancelling is what takes a click.
 *
 * It owns the timer but not the work — the mutation stays with the screen that opened
 * the snackbar, which outlives it, so the outcome is still reported once this message
 * has faded.
 */
const ReplayMigrationEventsCountdown = ({ onElapsed, onCancel }: ReplayMigrationEventsCountdownProps) => {
  const { t } = useTranslation()
  const [secondsLeft, setSecondsLeft] = useState(REPLAY_MIGRATION_COUNTDOWN_SECONDS)
  const [isCancelled, setIsCancelled] = useState(false)

  const isCancelledRef = useRef(false)
  const hasElapsedRef = useRef(false)
  const latestRef = useRef({ onElapsed, onCancel })
  latestRef.current = { onElapsed, onCancel }

  useEffect(() => {
    const intervalId = setInterval(() => setSecondsLeft((previous) => Math.max(0, previous - 1)), 1000)
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId)
      if (isCancelledRef.current) return

      hasElapsedRef.current = true
      latestRef.current.onElapsed()
    }, REPLAY_MIGRATION_COUNTDOWN_SECONDS * 1000)

    return () => {
      clearInterval(intervalId)
      clearTimeout(timeoutId)
      if (!hasElapsedRef.current && !isCancelledRef.current) latestRef.current.onCancel()
    }
  }, [])

  const handleCancel = () => {
    isCancelledRef.current = true
    setIsCancelled(true)
    latestRef.current.onCancel()
  }

  if (isCancelled) return t('loan.payment.replayMigrationEvents.cancelled')
  if (secondsLeft === 0) return t('loan.payment.replayMigrationEvents.running')

  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
      {t('loan.payment.replayMigrationEvents.pending', { count: secondsLeft })}
      <ActionButton size="small" onClick={handleCancel}>
        {t('loan.payment.replayMigrationEvents.cancel')}
      </ActionButton>
    </Stack>
  )
}

export default ReplayMigrationEventsCountdown
